import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { Inbox } from 'lucide-react';
import { HexOutline } from '@/components/brand/BrandMarks';

interface EmptyStateProps {
  /** Lucide icon component; defaults to Inbox */
  icon?: LucideIcon;
  /** Required headline */
  title: string;
  /** Optional explanatory paragraph */
  description?: string;
  /** Optional primary action */
  action?: { label: string; onClick: () => void };
  /** Padding density */
  compact?: boolean;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon = Inbox,
  title,
  description,
  action,
  compact = false,
}) => (
  <div
    className={`flex flex-col items-center justify-center text-center ${
      compact ? 'py-8' : 'py-16'
    } px-4`}
  >
    {/* Icon inside a faint Signal Cell outline — the brand's quiet echo on
        every empty surface. Pure decoration; the icon keeps its size. */}
    <span className="relative inline-flex items-center justify-center mb-4">
      <HexOutline size={76} className="text-fg-muted/25" />
      <Icon
        className="w-8 h-8 text-fg-muted absolute inset-0 m-auto"
        aria-hidden="true"
      />
    </span>
    <h2 className="text-base font-semibold text-fg-secondary">{title}</h2>
    {description && (
      <p className="mt-1 max-w-sm text-sm text-fg-muted">{description}</p>
    )}
    {action && (
      <button
        type="button"
        onClick={action.onClick}
        className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-accent hover:bg-accent-hover text-white font-semibold text-sm px-3 py-2 min-h-11 sm:min-h-0 transition-colors"
      >
        {action.label}
      </button>
    )}
  </div>
);

export default EmptyState;
